import { FC, useEffect, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import { Box } from "@/components/ui";
import { CountryValue } from "@/types/resources";

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const NUMERIC_TO_ISO2: Record<string, CountryValue> = {
  "036": "au",
  "040": "at",
  "056": "be",
  "076": "br",
  "100": "bg",
  "124": "ca",
  "156": "cn",
  "203": "cz",
  "208": "dk",
  "246": "fi",
  "250": "fr",
  "276": "de",
  "300": "gr",
  "344": "hk",
  "348": "hu",
  "356": "in",
  "360": "id",
  "372": "ie",
  "380": "it",
  "392": "jp",
  "398": "kz",
  "410": "kr",
  "458": "my",
  "484": "mx",
  "528": "nl",
  "578": "no",
  "616": "pl",
  "620": "pt",
  "642": "ro",
  "643": "ru",
  "688": "rs",
  "702": "sg",
  "724": "es",
  "752": "se",
  "756": "ch",
  "792": "tr",
  "804": "ua",
  "826": "gb",
  "840": "us",
};

interface MapLoaderProps {
  countries: CountryValue[];
}

const pickRandom = (arr: CountryValue[], n: number): Set<CountryValue> => {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return new Set(shuffled.slice(0, n));
};

const MapLoader: FC<MapLoaderProps> = ({ countries }) => {
  const [activeSet, setActiveSet] = useState<Set<CountryValue>>(new Set());

  useEffect(() => {
    if (countries.length === 0) return;
    const randomCount = () => Math.ceil(Math.random() * countries.length);
    const tick = () => setActiveSet(pickRandom(countries, randomCount()));
    // Defer the first pick to a timer so the random state update doesn't run
    // synchronously inside the effect.
    const timeout = setTimeout(tick, 0);
    const interval = setInterval(tick, 700);
    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, [countries]);

  const countrySet = new Set(countries);

  return (
    <Box>
      <div
        style={{ aspectRatio: "2 / 1", position: "relative", width: "100%" }}
      >
        <ComposableMap
          height={440}
          projectionConfig={{ center: [10, 15], scale: 158 }}
          style={{
            backgroundColor: "rgb(var(--color-background-0))",
            height: "100%",
            left: 0,
            position: "absolute",
            top: 0,
            width: "100%",
          }}
        >
          <Geographies geography={GEO_URL}>
            {({
              geographies,
            }: {
              geographies: { id: string; rsmKey: string }[];
            }) =>
              geographies.map((geo) => {
                if (geo.id === "010") return null; // Antarctica
                const iso2 = NUMERIC_TO_ISO2[geo.id];
                const isActive = iso2 && activeSet.has(iso2);
                const isTargeted = iso2 && countrySet.has(iso2);
                return (
                  <Geography
                    geography={geo}
                    key={geo.rsmKey}
                    style={{
                      default: {
                        fill: isActive
                          ? "rgb(var(--color-primary-500))"
                          : isTargeted
                            ? "rgb(var(--color-primary-200))"
                            : "rgb(var(--color-background-100))",
                        outline: "none",
                        stroke: "rgb(var(--color-background-0))",
                        strokeWidth: 0.5,
                        transition: "fill 0.25s ease",
                      },
                      hover: {
                        fill: isActive
                          ? "rgb(var(--color-primary-500))"
                          : isTargeted
                            ? "rgb(var(--color-primary-200))"
                            : "rgb(var(--color-background-100))",
                        outline: "none",
                        stroke: "rgb(var(--color-background-0))",
                        strokeWidth: 0.5,
                      },
                      pressed: {
                        fill: "rgb(var(--color-primary-300))",
                        outline: "none",
                        stroke: "rgb(var(--color-background-0))",
                        strokeWidth: 0.5,
                      },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>
    </Box>
  );
};

export default MapLoader;
